import React, { useEffect, useState } from 'react';
import { BrandMapping, BrandMappingKind } from '../../../server/types/brandMapping';
import { createPortal } from 'react-dom';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { ObjectId } from 'bson';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { updateBrandMappings } from '@go-client/lib/api/brandMapping';

interface BrandMappingsFormDataBase {
  port: BrandMapping[];
  property: BrandMapping[];
  sailing: BrandMapping[];
}

type BrandMappingFormData = BrandMappingsFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandMappingFormError>;
};

type BrandMappingFormError = { [K in keyof BrandMappingFormData]: string };

function mapToFormElement(mapping: BrandMapping): BrandMappingFormElement {
  return {
    key: mapping._id,
    refKey: mapping._id,
    item: mapping,
  };
}

function DEFAULT_FORM_DATA(brandMappings: BrandMapping[]): BrandMappingFormData {
  return {
    port: brandMappings.filter(mapping => mapping.kind === 'port') ?? [],
    property: brandMappings.filter(mapping => mapping.kind === 'property') ?? [],
    sailing: brandMappings.filter(mapping => mapping.kind === 'sailing') ?? [],
    _dirty: false,
    _errors: {},
  };
}

interface BrandMappingsFormProps {
  edit: BrandMapping[];
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

type BrandMappingFormElement = {
  key: string;
  refKey: string;
  item: BrandMapping;
};
export default function BrandMappingsForm(props: BrandMappingsFormProps) {
  const { brand } = useServerInfo();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandMappingFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandMappingsFormDataBase>(
    key: T,
    val: BrandMappingsFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        const mappings = [...formData.sailing, ...formData.property, ...formData.port];

        await updateBrandMappings(brand._id, mappings);
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const mappings: Record<BrandMappingKind, BrandMappingFormElement[]> = {
    port: formData.port.filter(mapping => mapping.kind === 'port').map(mapToFormElement) || [],
    property:
      formData.property.filter(mapping => mapping.kind === 'property').map(mapToFormElement) || [],
    sailing:
      formData.sailing.filter(mapping => mapping.kind === 'sailing').map(mapToFormElement) || [],
  };

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      {Object.keys(mappings).map(kind => {
        const label = `${kind} mapping`;
        const options = mappings[kind as BrandMappingKind];

        return (
          <BubbleFormGroup label={label} className="col-span-3" key={kind}>
            <BubbleTypeahead
              block
              options={options}
              placeholder="Enter new value..."
              menuClassName="max-h-80 overflow-y-auto"
              allowNew
              noSuggestionsClick
              alwaysShowSuggestions={options.length > 0}
              suggestionsText="No similar internalId found"
              suggestionClassName="p-1-i"
              suggestionsRenderFn={(_, opt) => (
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <span className="text-xs font-semibold uppercase text-navy mr-3">
                      Internal Id
                    </span>
                    <BubbleInput
                      type="text"
                      block
                      appearance="bare"
                      value={opt.item.internalId}
                      className="flex-grow mr-3"
                      inputClassName="px-3 py-1 rounded"
                      onValue={val => {
                        const arIdx = formData[kind as BrandMappingKind].findIndex(
                          s => s._id === opt.item._id
                        );
                        if (arIdx < 0) return;
                        const newValue = [...formData[kind as BrandMappingKind]];
                        newValue.splice(arIdx, 1, { ...opt.item, internalId: val });
                        changeForm(kind as BrandMappingKind, newValue);
                      }}
                    />

                    <span className="text-xs font-semibold uppercase text-navy mr-3">
                      External Id
                    </span>
                    <BubbleInput
                      type="text"
                      block
                      appearance="bare"
                      value={opt.item.externalId}
                      className="flex-grow mr-3"
                      inputClassName="px-3 py-1 rounded"
                      onValue={val => {
                        const arIdx = formData[kind as BrandMappingKind].findIndex(
                          s => s._id === opt.item._id
                        );
                        if (arIdx < 0) return;
                        const newValue = [...formData[kind as BrandMappingKind]];
                        newValue.splice(arIdx, 1, { ...opt.item, externalId: val });
                        changeForm(kind as BrandMappingKind, newValue);
                      }}
                    />
                  </div>

                  {opt.item.data && (
                    <div className="flex items-center">
                      <span className="text-xs font-semibold uppercase text-navy mr-3">Data</span>
                      <pre>{JSON.stringify(opt.item.data)}</pre>
                    </div>
                  )}
                </div>
              )}
              onCreate={val =>
                changeForm(kind as BrandMappingKind, [
                  ...formData[kind as BrandMappingKind],
                  {
                    _id: new ObjectId().toHexString(),
                    internalId: val,
                    externalId: '',
                    brandId: brand._id,
                    kind: kind as BrandMappingKind,
                  },
                ])
              }
              onTrash={val =>
                changeForm(
                  kind as BrandMappingKind,
                  formData[kind as BrandMappingKind].filter(ar => ar._id !== val.item._id)
                )
              }
            />
          </BubbleFormGroup>
        );
      })}
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
